import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  adminContact as getContact,
  adminUpdateContact as updateContact,
  adminDeleteContact as deleteContact,
  adminContactNote, adminSendPersonalEmail,
  adminCreateHousehold,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowLeft, Mail, User as UserIcon, MapPin, Home,
  Send, StickyNote, MessageSquare, Calendar, CheckSquare, Zap,
  Trash2, Save, Pencil, Users as UsersIcon,
} from "lucide-react";

function KV({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-sm text-stone-800">{children || <span className="text-stone-400">—</span>}</div>
    </div>
  );
}

function ComposeEmail({ open, onClose, contact }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && contact?.name) {
      const first = contact.name.split(" ")[0];
      if (!subject) setSubject(`A quick note from the Colorado Catholic Conference`);
      if (!body)    setBody(`Dear ${first},\n\n\n\nWith gratitude,\nColorado Catholic Conference`);
    }
    // eslint-disable-next-line
  }, [open, contact?.id]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      const html = body.split("\n\n").map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      const r = await adminSendPersonalEmail(contact.id, subject.trim(), html);
      toast.success(r.mocked ? "Sent (mocked — Resend not configured)" : `Sent to ${r.to}`);
      setSubject(""); setBody("");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="compose-personal-email">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ccc-plum">Send a personal note</DialogTitle>
          <DialogDescription>To: {contact?.name} &lt;{contact?.email || <em>no email</em>}&gt;</DialogDescription>
        </DialogHeader>
        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"
          data-testid="compose-personal-subject" className="mb-2"/>
        <Textarea value={body} onChange={e => setBody(e.target.value)} rows={12}
          data-testid="compose-personal-body" placeholder="Write your message…"/>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending || !contact?.email} data-testid="btn-send-personal">
            <Send size={14} className="mr-1.5"/>{sending ? "Sending…" : "Send email"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [composing, setComposing] = useState(false);
  const [linkingHousehold, setLinkingHousehold] = useState(false);
  const [linkNames, setLinkNames] = useState("");

  const load = async () => {
    setLoading(true);
    try { setData(await getContact(id)); }
    catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (data?.contact) setDraft(data.contact); }, [data]);

  if (loading) return <div className="p-8 text-stone-500 text-sm">Loading…</div>;
  if (!data?.contact) return <div className="p-8 text-stone-500 text-sm">Contact not found.</div>;

  const c = data.contact;

  const saveEdit = async () => {
    try {
      await updateContact(c.id, {
        name: draft.name, email: draft.email, phone: draft.phone, zip: draft.zip,
        parish: draft.parish, diocese: draft.diocese, contact_type: draft.contact_type,
      });
      toast.success("Contact updated");
      setEditing(false); load();
    } catch { toast.error("Save failed"); }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    try { await adminContactNote(c.id, note.trim()); setNote(""); toast.success("Note added"); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try { await deleteContact(c.id); toast.success("Deleted"); nav("/admin/contacts"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const linkHousehold = async () => {
    // Simple household create — accept a comma-separated list of contact IDs
    // for now; a proper picker would need a contact search combobox.
    const ids = [c.id, ...linkNames.split(",").map(s => s.trim()).filter(Boolean)];
    if (ids.length < 2) { toast.error("Enter at least one other contact ID"); return; }
    try {
      const r = await adminCreateHousehold(ids);
      toast.success(`Linked into ${r.name}`);
      setLinkingHousehold(false); setLinkNames(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="contact-detail-page">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/contacts" className="inline-flex items-center gap-1 text-xs text-ccc-purple hover:underline mb-3">
          <ArrowLeft size={12}/> Back to contacts
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-ccc-gold">{c.contact_type || "Advocate"}</div>
            <h1 className="font-serif text-4xl text-ccc-plum" data-testid="contact-name">{c.name || "(no name)"}</h1>
            <div className="text-sm text-stone-600 flex items-center gap-3 mt-1">
              {c.email && <span className="inline-flex items-center gap-1"><Mail size={12}/>{c.email}</span>}
              {c.parish && <span className="inline-flex items-center gap-1"><MapPin size={12}/>{c.parish}</span>}
              <span className="inline-flex items-center gap-1"><Zap size={12}/>{c.engagement_score ?? 0} engagement</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setComposing(true)} disabled={!c.email} data-testid="btn-personal-email">
              <Send size={14} className="mr-1.5"/>Send email
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} data-testid="btn-edit-contact">
              <Pencil size={14} className="mr-1.5"/>{editing ? "Cancel" : "Edit"}
            </Button>
            <Button size="sm" variant="outline" onClick={del} data-testid="btn-delete-contact">
              <Trash2 size={14} className="mr-1.5 text-red-500"/>Delete
            </Button>
          </div>
        </div>
      </div>

      {editing ? (
        <div className="bg-white border border-stone-200 p-6 mb-6 grid grid-cols-2 gap-3">
          {["name","email","phone","zip","parish","diocese","contact_type"].map(k => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-widest text-stone-500">{k.replace("_", " ")}</label>
              <Input value={draft[k] || ""} onChange={e => setDraft(prev => ({ ...prev, [k]: e.target.value }))} data-testid={`edit-${k}`} />
            </div>
          ))}
          <div className="col-span-2 flex justify-end">
            <Button size="sm" onClick={saveEdit} data-testid="btn-save-edit"><Save size={14} className="mr-1.5"/>Save</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-white border border-stone-200 p-4">
          <KV label="Email">{c.email}</KV>
          <KV label="Phone">{c.phone}</KV>
          <KV label="Zip">{c.zip}</KV>
          <KV label="Diocese">{c.diocese}</KV>
          <KV label="House district">{c.house_district}</KV>
          <KV label="Senate district">{c.senate_district}</KV>
          <KV label="Tags">{c.tags?.length ? c.tags.join(", ") : null}</KV>
          <KV label="Source">{c.source}</KV>
        </div>
      )}

      {/* Household */}
      <div className="bg-white border border-stone-200 p-4 mb-6" data-testid="household-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-ccc-purple flex items-center gap-1"><Home size={12}/>Household</div>
          {!data.household && (
            <Button size="sm" variant="outline" onClick={() => setLinkingHousehold(true)} data-testid="btn-link-household">
              <UsersIcon size={12} className="mr-1"/>Link to household
            </Button>
          )}
        </div>
        {data.household ? (
          <div>
            <div className="text-sm text-stone-800 font-medium">{data.household.members?.length || 0} members</div>
            <div className="text-xs text-stone-500 mb-2">household id: {data.household.id}</div>
            <div className="flex flex-wrap gap-2">
              {data.household.members.map(m => (
                <Link key={m.id} to={`/admin/contacts/${m.id}`}
                  className={`px-3 py-1 border text-xs ${m.id === c.id ? "border-ccc-purple bg-ccc-purple/5 text-ccc-purple" : "border-stone-200 hover:border-ccc-purple"}`}>
                  {m.name} · {m.engagement_score ?? 0}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-xs text-stone-400 italic">Not in a household.</div>
        )}
      </div>

      {/* Tabs — activity / emails / actions / events / conversations / tasks / notes */}
      <Tabs defaultValue="activity" data-testid="contact-tabs">
        <TabsList>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Emails ({data.emails_sent?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions">Actions ({data.action_events?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Events ({data.rsvps?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="chats" data-testid="tab-chats">AI Chats ({data.conversations?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({data.tasks?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({c.notes?.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(data.activities || []).map(a => (
              <div key={a.id} className="p-3 flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-ccc-plum/8 text-ccc-plum flex items-center justify-center text-[10px]">•</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-800">{a.detail || a.type}</div>
                  <div className="text-xs text-stone-500">{a.type} · {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</div>
                </div>
              </div>
            ))}
            {(data.activities || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No activity yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="emails">
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(data.emails_sent || []).map(e => (
              <div key={e.id} className="p-3">
                <div className="text-sm text-stone-800">{e.subject}</div>
                <div className="text-xs text-stone-500">{e.job || "manual"} · {formatDistanceToNow(new Date(e.sent_at || e.created_at), { addSuffix: true })}</div>
              </div>
            ))}
            {(data.emails_sent || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No emails sent yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="actions">
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(data.action_events || []).map(a => (
              <div key={a.id} className="p-3">
                <div className="text-sm text-stone-800">{a.subject || a.alert_slug || a.type}</div>
                <div className="text-xs text-stone-500">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</div>
              </div>
            ))}
            {(data.action_events || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No advocacy actions yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="events">
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(data.rsvps || []).map(r => (
              <div key={r.id} className="p-3">
                <div className="text-sm text-stone-800">{r.event_title || r.event_slug || "Event RSVP"}</div>
                <div className="text-xs text-stone-500">{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</div>
              </div>
            ))}
            {(data.rsvps || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No RSVPs yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="chats">
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(data.conversations || []).map(s => (
              <Link to="/admin/conversations" key={s.session_id} className="p-3 flex items-center gap-3 hover:bg-stone-50">
                <MessageSquare size={14} className="text-ccc-purple"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-800">Session · {s.turns} turns</div>
                  <div className="text-xs text-stone-500 truncate">{s.session_id} · {formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}</div>
                </div>
              </Link>
            ))}
            {(data.conversations || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No AI assistant sessions linked.</div>}
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(data.tasks || []).map(t => (
              <div key={t.id} className="p-3">
                <div className="text-sm text-stone-800">{t.title}</div>
                <div className="text-xs text-stone-500">{t.status} · due {t.due_date || "—"}</div>
              </div>
            ))}
            {(data.tasks || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No tasks linked.</div>}
          </div>
        </TabsContent>

        <TabsContent value="notes">
          <div className="bg-white border border-stone-200 p-4 mb-3">
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="Add an internal note…" data-testid="contact-note-input"/>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={saveNote} data-testid="btn-save-note"><StickyNote size={14} className="mr-1.5"/>Save note</Button>
            </div>
          </div>
          <div className="bg-white border border-stone-200 divide-y divide-stone-100">
            {(c.notes || []).map(n => (
              <div key={n.id} className="p-3">
                <div className="text-sm text-stone-800 whitespace-pre-wrap">{n.body}</div>
                <div className="text-xs text-stone-500 mt-1">{n.author_name || n.author_email} · {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</div>
              </div>
            ))}
            {(c.notes || []).length === 0 && <div className="p-6 text-sm text-stone-400 italic">No notes yet.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <ComposeEmail open={composing} onClose={() => setComposing(false)} contact={c} />

      <Dialog open={linkingHousehold} onOpenChange={(o) => !o && setLinkingHousehold(false)}>
        <DialogContent data-testid="link-household-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ccc-plum">Link to household</DialogTitle>
            <DialogDescription>Paste one or more other contact IDs, comma-separated. They'll all share a household with {c.name}.</DialogDescription>
          </DialogHeader>
          <Textarea value={linkNames} onChange={e => setLinkNames(e.target.value)} rows={3}
            placeholder="contact_abcd1234, contact_efgh5678" data-testid="link-household-ids"/>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setLinkingHousehold(false)}>Cancel</Button>
            <Button onClick={linkHousehold} data-testid="btn-confirm-link-household">Link household</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
